import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react"
import { createPortal } from "react-dom"
import { CheckIcon, ChevronDownIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import Checkbox from "aldoo-ra/Checkbox"
import { VariantReader } from "aldoo-ra/VariantReader"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import ModalStack from "aldoo-ra/Modal/ModalStack"
import { twMerge } from "tailwind-merge"

let dropdownStackCounter = 0

const DropDownSelect = forwardRef(
  (
    {
      options = [],
      value = [],
      styleOverrides,
      placeholder = "Select...",
      onChange,
      onItemRender,
      renderOption,
      itemClassName = "",
      valueField = "value",
      menuPosition = "bottom",
      multiSelection = false,
      shouldResetSelection = false,
      // When omitted, the portal panel registers itself with ModalStack on
      // open and uses the assigned z-index, the same way a Modal does. This
      // keeps the menu above whatever modal contains its trigger without
      // callers having to hard-code z values.
      zIndex,
      fixedWidth,
      showItemNamesInSelection = false,
      searchEnabled = false,
      maxHeight, // New prop for controlling maximum height
      variant = "primary", // Add variant prop with primary as default
      className = "", // Add className prop
      onSearch, // Add onSearch prop for external search handling
      loading = false, // Add loading prop
      error, // Add error prop
      skipInitialLoad = false, // Skip initial load if parent handles it
      useExternalSearch = false, // Use external search instead of local filtering
      enableResetButton = true, // Enable/disable the reset button
      showCheckbox = true, // Show/hide checkboxes in menu items
      showSelectionTick = false, // Show/hide selection tick (like DateFilterDropdown)
      showSelectAll = true, // Show/hide the "Select All" option in multi-selection dropdowns
      highlightOnSelection = false, // Add outline when value is selected
      highlightResetValue = null, // Value that acts as "no selection" for highlighting
      localUserPreferencesKey = null, // Key for storing user preferences
      resetLocalUserPreferences = false, // Reset control value from preferences
      onValueItemRender, // Custom render function for the selected value display
      creatable = false, // Allow creating custom values from search query
      editable = false, // Render as an input field (Combobox) instead of a button
      customTrigger, // Custom element to act as the trigger
      dropdownWidth, // Custom width for the dropdown panel
      
      // New DataTable-like props
      apiMethod, // API method to fetch data
      searchField, // Fields to search on (space-separated like DataTable)
      apiDataField = "data", // Field in API response containing the data array
      nameField = "name", // Field to use for display name
      requestFields = {}, // Additional fields to pass to API
      onDataLoaded, // Callback to transform raw API data into dropdown format
      noDataMessage = "No options available", // Message when no options are available
      allowEmptySelection = true, // Allow deselection by clicking selected item
      showChevron = true, // Show/hide the chevron icon on the trigger button
      disabled = false, // Disable the trigger button entirely
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [showSearch, setShowSearch] = useState(false)
    const [searchQuery, setSearchQuery] = useState("")
    const [filterQuery, setFilterQuery] = useState("") // Separate filter state
    const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0, width: 0, showAbove: false })
    const [stackZ, setStackZ] = useState(null)
    const dropdownRef = useRef(null)
    const buttonRef = useRef(null)
    const searchInputRef = useRef(null)
    const lockedShowAbove = useRef(null) // Lock position direction once dropdown opens
    const stackIdRef = useRef(null)
    if (!stackIdRef.current) stackIdRef.current = `dropdown-select-${++dropdownStackCounter}`

    useImperativeHandle(ref, () => ({ close: () => setIsOpen(false) }))

    // Walk up from the trigger to find the nearest ancestor that
    // establishes a stacking context with an explicit z-index. That value
    // is the ceiling the portalled panel must beat regardless of whether
    // the ancestor is a Modal, drawer, sticky header, custom popover, etc.
    // We also consult ModalStack so a dropdown inside a modal sits above
    // any sibling modals opened on top.
    const computeParentZ = () => {
      let max = 0
      const visit = (el) => {
        if (!el || el === document.body) return
        const cs = window.getComputedStyle(el)
        const z = parseInt(cs.zIndex, 10)
        if (!Number.isNaN(z)) max = Math.max(max, z)
        visit(el.parentElement)
      }
      visit(buttonRef.current?.parentElement)
      const top = ModalStack.getTop()
      if (top?.zIndex) max = Math.max(max, top.zIndex)
      return max
    }

    useEffect(() => {
      if (!isOpen) {
        if (stackZ !== null) {
          ModalStack.remove(stackIdRef.current)
          setStackZ(null)
        }
        return
      }
      // Push onto ModalStack so the panel participates in the global
      // z-ordering and closes on Escape alongside modals. Then bump the
      // assigned z above any stacking context discovered higher up in the
      // DOM (a non-modal popover, drawer, etc.) so we cover every case.
      const assigned = ModalStack.push(stackIdRef.current, () => setIsOpen(false), false)
      const parentCeiling = computeParentZ()
      setStackZ(Math.max(assigned, parentCeiling + 10))
      return () => {
        ModalStack.remove(stackIdRef.current)
      }
    }, [isOpen])
    
    // API-related state
    const [apiOptions, setApiOptions] = useState([])
    const [apiLoading, setApiLoading] = useState(false)
    const [apiError, setApiError] = useState(null)

    // Get styles from variant using VariantReader
    const styles = VariantReader.useVariant({
      componentName: "DropdownSelect",
      variant,
      styleOverrides,
    })

    // Make sure the value is array and objects are cloned to avoid reference issues
    const valueArray = useMemo(() => {
      let result
      if (Array.isArray(value)) {
        // Make a shallow copy of the array with deep copies of each object
        result = value.map((item) =>
          typeof item === "object" ? { ...item } : item
        )
      } else if (value) {
        // If it's a single value, wrap it in an array with a deep copy if it's an object
        result = [typeof value === "object" ? { ...value } : value]
      } else {
        result = []
      }
      return result
    }, [value])

    // Helper function to determine if selection should be highlighted
    const shouldHighlight = useMemo(() => {
      if (!highlightOnSelection || valueArray.length === 0) return false
      
      // For single selection, check if the selected value is different from reset value
      if (valueArray.length === 1) {
        const extractedValue = valueArray[0]?.[valueField]
        return extractedValue !== highlightResetValue
      }
      
      // For multi-selection, highlight if there are any selections
      return valueArray.length > 0
    }, [highlightOnSelection, valueArray, highlightResetValue, valueField])

    // API search function - using useRef to avoid dependency issues
    const performApiSearchRef = useRef()
    performApiSearchRef.current = async (searchTerm) => {
      if (!apiMethod) return
      
      setApiLoading(true)
      setApiError(null)
      
      try {
        // Pass simple parameters to the API method
        const apiParams = {
          searchTerm: searchTerm || '',
          limit: 50, // Reasonable limit for dropdown
          ...requestFields
        }
        
        const response = await apiMethod(apiParams)
        const rawData = response[apiDataField] || response.data || []
        
        // Transform data to dropdown format
        let formattedOptions
        if (onDataLoaded && typeof onDataLoaded === 'function') {
          // Use custom data transformation callback
          formattedOptions = onDataLoaded(rawData)
        } else {
          // Default transformation
          formattedOptions = rawData.map(item => ({
            [valueField]: item[valueField] || item._id,
            [nameField]: item[nameField] || item.name || item.email || 'Unknown',
            ...item // Keep original data
          }))
        }
        
        setApiOptions(formattedOptions)
      } catch (error) {
        console.error('API search error:', error)
        setApiError(error.message || 'Search failed')
        setApiOptions([])
      } finally {
        setApiLoading(false)
      }
    }

    // Load and manage local user preferences
    useEffect(() => {
      if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
        try {
          if (resetLocalUserPreferences) {
            // Reset preferences - clear the stored value
            LocalUserPreferences.delete(localUserPreferencesKey)
          } else {
            // Load preferences and update value if different from current
            const savedValue = LocalUserPreferences.get(localUserPreferencesKey)
            if (savedValue !== undefined && onChange) {
              // Only update if the saved value is different from current value
              const currentValueStr = JSON.stringify(valueArray)
              const savedValueStr = JSON.stringify(savedValue)
              if (currentValueStr !== savedValueStr) {
                onChange(savedValue)
              }
            }
          }
        } catch (error) {
          console.warn("Failed to load user preference:", error)
        }
      }
    }, [localUserPreferencesKey, resetLocalUserPreferences]) // Don't include onChange/valueArray to avoid loops

    // Count selectable options (excluding dividers)
    const selectableOptions = useMemo(() => {
      const filtered = options.filter((option) => !option.divider)
      return filtered
    }, [options])

    const selectableOptionsCount = useMemo(() => {
      return selectableOptions.length
    }, [selectableOptions])

    // Check if all selectable items are selected
    const allSelected = useMemo(() => {
      if (valueArray.length === 0) return false
      if (valueArray.length !== selectableOptionsCount) return false

      // Verify that all items in value are actually from the selectable options
      // We compare by valueField to avoid reference issues
      const valueIds = new Set(valueArray.map((item) => item?.[valueField]))

      // Check if all selectable options have their valueField in the selected values
      const allIncluded = selectableOptions.every((option) =>
        valueIds.has(option?.[valueField])
      )

      return allIncluded
    }, [valueArray, selectableOptions, selectableOptionsCount, valueField])


    const toggleDropdown = () => {
      if (disabled) return
      if (!isOpen) {
        // Reset the locked position
        lockedShowAbove.current = null

        // Calculate position BEFORE opening dropdown to prevent flash at (0,0)
        updatePositioning()

        // Opening dropdown
        setIsOpen(true)
        // Reset filter query on open
        setFilterQuery("")
        
        // Initialize search query with current value if editable, otherwise clear it
        if (editable) {
            const hasValue = valueArray.length > 0 && valueArray[0] && (typeof valueArray[0] !== 'object' || Object.keys(valueArray[0]).length > 0);
            if (hasValue) {
                const label = getDisplayLabel;
                setSearchQuery(typeof label === 'string' ? label : nameOf(valueArray[0]));
            } else {
                setSearchQuery("");
            }
        } else {
            setSearchQuery("");
        }

        // Load initial data if external search is provided and no options exist
        if (onSearch && useExternalSearch && options.length === 0 && !skipInitialLoad) {
          onSearch("")
        }
        if (searchEnabled || apiMethod) {
          setShowSearch(true)
          // Only focus the search input if NOT in editable mode
          // In editable mode, the main input should keep focus
          if (!editable) {
            setTimeout(() => {
              if (searchInputRef.current) {
                searchInputRef.current.focus()
              }
            }, 100)
          }
        }
      } else {
        // Closing dropdown
        setIsOpen(false)
        setShowSearch(false)
        lockedShowAbove.current = null // Reset lock
        if (searchQuery) {
          setSearchQuery("")
          // Don't call external search on close - let the parent handle initial data
        }
      }
    }

    const resetSelection = (e) => {
      if (e) {
        e.stopPropagation()
      }
      
      let resetValue = []
      
      // If empty selection not allowed, select the first option
      if (!allowEmptySelection && !multiSelection && options.length > 0) {
        const firstSelectableOption = options.find(option => !option.divider && !option.disabled)
        if (firstSelectableOption) {
          resetValue = [firstSelectableOption]
        }
      }
      
      onChange && onChange(resetValue)
      
      // Save to local user preferences if key is provided
      if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
        try {
          LocalUserPreferences.set(localUserPreferencesKey, resetValue)
        } catch (error) {
          console.warn("Failed to save user preference:", error)
        }
      }
    }

    const handleCheckboxChange = (option, checked) => {
      // Skip handling dividers
      if (option.divider) return

      let updatedSelection
      if (multiSelection) {
        if (checked) {
          updatedSelection = [
            ...valueArray.filter(
              (item) => item?.[valueField] !== option?.[valueField]
            ),
            option,
          ]
        } else {
          updatedSelection = valueArray.filter(
            (item) => item?.[valueField] !== option?.[valueField]
          )
        }
      } else {
        if (!checked && !allowEmptySelection) {
          // If trying to deselect but empty selection not allowed, keep current selection
          updatedSelection = valueArray
        } else {
          updatedSelection = checked ? [option] : []
        }
        // Close dropdown immediately for single selection
        setIsOpen(false)
        setShowSearch(false)
        setSearchQuery("")
      }

      onChange && onChange(updatedSelection)
      
      // Save to local user preferences if key is provided
      if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
        try {
          LocalUserPreferences.set(localUserPreferencesKey, updatedSelection)
        } catch (error) {
          console.warn("Failed to save user preference:", error)
        }
      }
    }

    const handleSelectAll = (checked) => {
      if (!multiSelection) return

      // When checked is true, select all items
      if (checked) {
        // Create a completely new array with all selectable options
        // Using object spread to create new object references
        const optionsCopy = selectableOptions.map((option) => ({ ...option }))
        onChange && onChange(optionsCopy)
        
        // Save to local user preferences if key is provided
        if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
          try {
            LocalUserPreferences.set(localUserPreferencesKey, optionsCopy)
          } catch (error) {
            console.warn("Failed to save user preference:", error)
          }
        }
      } else {
        onChange && onChange([])
        
        // Save to local user preferences if key is provided
        if (localUserPreferencesKey && localUserPreferencesKey.trim()) {
          try {
            LocalUserPreferences.set(localUserPreferencesKey, [])
          } catch (error) {
            console.warn("Failed to save user preference:", error)
          }
        }
      }
    }

    const filteredOptions = useMemo(() => {
      // Use API options if available, otherwise use provided options
      const currentOptions = apiMethod ? apiOptions : options

      // If using external search, return options as-is (filtering handled by parent)
      if (useExternalSearch) return currentOptions

      // Local filtering
      // In editable mode, only use filterQuery (from dropdown search input)
      // searchQuery is for the main input and should not filter the dropdown
      const queryToUse = filterQuery;
      let results = !queryToUse ? currentOptions : currentOptions.filter((option) => {
        // Always include dividers in the filtered results
        if (option.divider) return true

        const searchValue = (
          option[nameField] ||
          option.name ||
          option.label ||
          option[valueField] ||
          ""
        ).toLowerCase()
        return searchValue.includes(queryToUse.toLowerCase())
      });

      // Don't show custom option in dropdown when editable
      // In editable mode, user can type freely in main input
      // The creatable feature is handled on blur/enter in the main input

      return results;
    }, [options, apiOptions, apiMethod, searchQuery, filterQuery, valueField, nameField, useExternalSearch, creatable, editable])

    // What the trigger shows for the selection.
    //
    // A selected item is rendered by `onValueItemRender`, else by
    // `onItemRender` (the menu row's renderer), else it is the item's name
    // (`nameField`, then name / label / the value). A renderer may return a
    // string or JSX; anything else — a component definition, a plain object —
    // cannot be shown, so the item's name stands in for it. Several selected
    // items are joined by name (JSX cannot be joined) or counted.
    const nameOf = (item) => {
      if (item === null || item === undefined) return ""
      if (typeof item !== "object") return String(item)
      const name = item[nameField] ?? item.name ?? item.label ?? item[valueField]
      return name === null || name === undefined ? "Unknown" : String(name)
    }

    const renderLabel = (render, item) => {
      if (!render) return nameOf(item)
      const result = render(item)
      if (React.isValidElement(result) || typeof result === "string" || typeof result === "number") return result
      if (result?.$$typeof) {
        console.error("[DropdownSelect] a value renderer returned a component definition instead of JSX — render it as <Component />:", result)
      }
      return nameOf(item)
    }

    const getDisplayLabel = useMemo(() => {
      const render = onValueItemRender || onItemRender
      const empty =
        !valueArray ||
        valueArray.length === 0 ||
        !valueArray[0] ||
        (typeof valueArray[0] === "object" && Object.keys(valueArray[0]).length === 0)

      if (empty) {
        // a value renderer draws the placeholder too (it is called with null)
        if (onValueItemRender) {
          const result = onValueItemRender(null)
          return React.isValidElement(result) || typeof result === "string" ? result : placeholder
        }
        return options && options.length ? placeholder : noDataMessage
      }

      if (valueArray.length === 1) return renderLabel(render, valueArray[0])

      if (!showItemNamesInSelection) return `${valueArray.length} selected`
      return valueArray
        .map((item) => {
          const label = renderLabel(render, item)
          return typeof label === "string" || typeof label === "number" ? label : nameOf(item)
        })
        .join(", ")
    }, [
      valueArray,
      placeholder,
      noDataMessage,
      onItemRender,
      onValueItemRender,
      showItemNamesInSelection,
      valueField,
      nameField,
      options,
    ])

    // Calculate portal positions - always use portal for better z-index handling
    const updatePositioning = useCallback(() => {
      if (!buttonRef.current) return

      const buttonRect = buttonRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth

      // Extract gap from panel styles (e.g., "mt-2" -> 8px)
      let gap = 8 // Default fallback
      if (dropdownRef.current) {
        const panelStyles = window.getComputedStyle(dropdownRef.current)
        const marginTop = parseInt(panelStyles.marginTop) || 0
        if (marginTop > 0) {
          gap = marginTop
        }
      }

      // Get actual dropdown height if it exists, otherwise use default
      const dropdownHeight = dropdownRef.current
        ? dropdownRef.current.offsetHeight
        : 300

      // Get actual dropdown width to check for horizontal overflow
      let dropdownRealWidth = buttonRect.width
      if (dropdownRef.current) {
        dropdownRealWidth = dropdownRef.current.offsetWidth
      } else if (dropdownWidth) {
        if (typeof dropdownWidth === 'number') {
          dropdownRealWidth = dropdownWidth
        } else if (typeof dropdownWidth === 'string') {
          if (dropdownWidth.endsWith('rem')) {
             dropdownRealWidth = parseFloat(dropdownWidth) * 16 // Approximation
          } else if (dropdownWidth.endsWith('px')) {
             dropdownRealWidth = parseFloat(dropdownWidth)
          }
        }
      }

      // Check if there's enough space below
      const spaceBelow = viewportHeight - buttonRect.bottom - gap
      const spaceAbove = buttonRect.top - gap

      // Determine if we should show above or below
      let shouldShowAbove

      // If position is already locked (dropdown is open), use locked value
      if (lockedShowAbove.current !== null) {
        shouldShowAbove = lockedShowAbove.current
      } else {
        // First calculation - determine position and lock it
        shouldShowAbove = menuPosition === 'top'
        if (menuPosition === 'bottom' && spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          shouldShowAbove = true
        }
        // Lock the position for subsequent updates
        lockedShowAbove.current = shouldShowAbove
      }

      // Horizontal positioning logic
      let left = buttonRect.left
      
      // Check for right overflow
      if (left + dropdownRealWidth > viewportWidth - gap) {
          // Try aligning to the right edge of the button
          const rightAlignedLeft = buttonRect.right - dropdownRealWidth
          
          // If right-aligned still fits or is better (at least on screen), use it
          // Or just clamp to viewport width
          if (rightAlignedLeft >= gap) {
              left = rightAlignedLeft
          } else {
              // Clamp to right edge of viewport
              left = viewportWidth - dropdownRealWidth - gap
          }
      }
      
      // Ensure no left overflow
      if (left < gap) {
          left = gap
      }

      const position = {
        top: shouldShowAbove
          ? buttonRect.top - dropdownHeight - gap
          : buttonRect.bottom + gap,
        left: left,
        showAbove: shouldShowAbove,
      }

      // Only set width if not using custom value renderer
      if (!onValueItemRender) {
        position.width = buttonRect.width
      } else {
        position.minWidth = buttonRect.width
      }

      setPortalPosition(position)
    }, [menuPosition, onValueItemRender, dropdownWidth])


    // Update positioning when dropdown opens (now handled in toggleDropdown)
    useEffect(() => {
      if (isOpen) {
        // Position was already calculated in toggleDropdown, but update again
        // in case the component moved between clicks
        updatePositioning()
      }
    }, [isOpen, updatePositioning])

    // Update positioning when filtered options change (for showAbove scenarios)
    useEffect(() => {
      if (isOpen && portalPosition.showAbove) {
        // Need to update position as dropdown height changes with filtering
        updatePositioning()
      }
    }, [filteredOptions.length, isOpen, portalPosition.showAbove, updatePositioning])


    // Handle external search with debouncing
    useEffect(() => {
      if (onSearch && useExternalSearch && searchQuery) {
        const debounceTimer = setTimeout(() => {
          onSearch(searchQuery)
        }, 300) // 300ms debounce

        return () => clearTimeout(debounceTimer)
      }
    }, [searchQuery, onSearch, useExternalSearch])

    // Handle API search with debouncing
    useEffect(() => {
      if (apiMethod) {
        if (searchQuery) {
          const debounceTimer = setTimeout(() => {
            performApiSearchRef.current(searchQuery)
          }, 300) // 300ms debounce

          return () => clearTimeout(debounceTimer)
        } else {
          // Clear API options when search is empty
          setApiOptions([])
        }
      }
    }, [searchQuery, apiMethod])

    // Load initial API data when dropdown opens
    useEffect(() => {
      if (apiMethod && isOpen && !searchQuery && apiOptions.length === 0) {
        performApiSearchRef.current('')
      }
    }, [apiMethod, isOpen, searchQuery, apiOptions.length])

    // Handle search input focus
    useEffect(() => {
      // Only auto-focus internal search if NOT in editable mode
      // In editable mode, user must explicitly click the search input
      if (isOpen && (searchEnabled || apiMethod) && showSearch && !editable) {
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus()
          }
        }, 150) // Always use portal timing
      }
    }, [isOpen, searchEnabled, showSearch, editable, apiMethod])

    // Center the selected option in the panel when the dropdown opens
    useEffect(() => {
      if (!isOpen) return
      const id = setTimeout(() => {
        const panel = dropdownRef.current
        const sel = panel && panel.querySelector('[data-selected="1"]')
        if (sel) sel.scrollIntoView({ block: "center", inline: "nearest" })
      }, 60)
      return () => clearTimeout(id)
    }, [isOpen])

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)
        ) {
          setIsOpen(false)
          setShowSearch(false)
          if (searchQuery) {
            setSearchQuery("")
            // Don't call external search on close - let the parent handle initial data
          }
        }
      }

      // Handle scroll events to reposition portal dropdowns
      const handleScroll = () => {
        if (isOpen) {
          updatePositioning()
        }
      }

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside)
        window.addEventListener("scroll", handleScroll, true)
        window.addEventListener("resize", handleScroll)
      } else {
        document.removeEventListener("mousedown", handleClickOutside)
        window.removeEventListener("scroll", handleScroll, true)
        window.removeEventListener("resize", handleScroll)
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside)
        window.removeEventListener("scroll", handleScroll, true)
        window.removeEventListener("resize", handleScroll)
      }
    }, [isOpen, menuPosition])

    // Render dropdown panel content
    const renderDropdownPanel = () => {
      // A dropdown menu must always float above its host modal. `stackZ` is the
      // ModalStack-computed z that beats the current modal (ModalStack.Z_BASE is
      // ~1.05e9). An explicit `zIndex` prop is honored only when it's HIGHER —
      // otherwise a hardcoded low value (e.g. the legacy 410/420 from before the
      // modal-stack change) would sink the menu behind the modal. Take the max
      // of both, falling back to a value above any stack we'd realistically push.
      const resolvedZ = Math.max(zIndex ?? 0, stackZ ?? 0) || 1200000000
      // Always use portal positioning with fixed styles
      const portalStyles = {
        position: 'fixed',
        top: `${portalPosition.top}px`,
        left: `${portalPosition.left}px`,
        boxSizing: 'border-box', // Ensure padding is included in width
        zIndex: resolvedZ,
        ...(dropdownWidth
          ? { width: typeof dropdownWidth === 'number' ? `${dropdownWidth}px` : dropdownWidth }
          : {
              ...(portalPosition.width && {
                width: `${portalPosition.width}px`,
                maxWidth: `${portalPosition.width}px`,
                minWidth: `${portalPosition.width}px`,
              }),
              ...(portalPosition.minWidth && !portalPosition.width && {
                minWidth: `${portalPosition.minWidth}px`,
              }),
            }),
      }

      return (
        <div
          ref={dropdownRef}
          className={styles.panel}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
          style={portalStyles}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
          onBlur={(e) => e.stopPropagation()}
        >
          {/* Search Input - show at bottom when panel is above */}
          {(searchEnabled || apiMethod) && showSearch && !portalPosition.showAbove && (
            <div className={styles.searchContainer}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search..."
                value={apiMethod || useExternalSearch ? searchQuery : filterQuery}
                onChange={(e) => {
                  if (apiMethod || useExternalSearch) {
                    setSearchQuery(e.target.value)
                  } else {
                    setFilterQuery(e.target.value)
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter' && filteredOptions.length > 0) {
                        handleCheckboxChange(filteredOptions[0], true);
                    }
                }}
              />
            </div>
          )}

          <div
            className={styles.menuContainer}
            style={maxHeight ? { maxHeight } : undefined}
          >
            {/* Loading indicator */}
            {(loading || apiLoading) && (
              <div className="px-4 py-2 text-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-admin_primary mx-auto"></div>
              </div>
            )}

            {/* Select All Checkbox (only if multi-selection is enabled and showSelectAll is true) */}
            {multiSelection && showSelectAll && selectableOptionsCount > 1 && !loading && !apiLoading && (
              <div
                className={styles.selectAllContainer}
                onClick={(e) => {
                  e.stopPropagation()
                  // Force inversion of current selection state with a direct check
                  if (valueArray.length === selectableOptionsCount) {
                    // If we have the same number of items selected as options
                    // (might not be exactly the same items), then deselect all
                    handleSelectAll(false)
                  } else {
                    // Otherwise, select all items
                    handleSelectAll(true)
                  }
                }}
              >
                <Checkbox
                  labelClassName={itemClassName}
                  isOn={allSelected}
                  onChange={(checked) => {
                    handleSelectAll(checked)
                  }}
                  label={allSelected ? "Deselect All" : "Select All"}
                />
                <div className={styles.divider}></div>
              </div>
            )}

            {/* Render filtered options */}
            {!loading && !apiLoading && Array.isArray(filteredOptions) &&
              filteredOptions.map((option, index) => {
                // Handle divider items
                if (option.divider) {
                  return (
                    <div key={`divider-${index}`} className="px-1 py-1">
                      <div className={styles.divider}></div>
                    </div>
                  )
                }

                // Handle regular items
                const isSelected =
                  valueArray &&
                  valueArray.some(
                    (item) =>
                      item &&
                      option &&
                      item[valueField] === option[valueField]
                  )

                // Use custom renderer if provided
                if (renderOption) {
                  return (
                    <div
                      key={index}
                      data-selected={isSelected ? "1" : undefined}
                      className={
                        !option.disabled
                          ? styles.menuItem
                          : styles.menuItemDisabled
                      }
                      onClick={() => {
                        if (!option.disabled) {
                          handleCheckboxChange(option, !isSelected)
                        }
                      }}
                    >
                      {renderOption(option)}
                    </div>
                  )
                }

                return (
                  <div
                    key={index}
                    data-selected={isSelected ? "1" : undefined}
                    className={
                      !option.disabled
                        ? (isSelected && styles.menuItemSelected ? styles.menuItemSelected : styles.menuItem)
                        : styles.menuItemDisabled
                    }
                    onClick={() => {
                      if (!option.disabled) {
                        handleCheckboxChange(option, !isSelected)
                      }
                    }}
                  >
                    {showCheckbox ? (
                      <Checkbox
                        labelClassName={itemClassName}
                        isOn={isSelected}
                        onChange={(checked) => {
                          if (!option.disabled) {
                            handleCheckboxChange(option, checked)
                          }
                        }}
                        label={
                          onItemRender
                            ? onItemRender(option)
                            : option.name || option.label || option[valueField]
                        }
                        multiSelection={multiSelection}
                        disabled={option.disabled}
                      />
                    ) : (
                      <div className="flex items-center justify-between w-full">
                        {onItemRender ? (
                          <>
                            {onItemRender(option)}
                            {showSelectionTick && isSelected && (
                              <CheckIcon className="w-5 h-5 ml-auto" />
                            )}
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              {/* a picture of what the option is — a creator's
                                  photo, a platform's logo — when it carries one */}
                              {option.image && (
                                <img
                                  src={Array.isArray(option.image) ? option.image[0] : option.image}
                                  alt=""
                                  loading="lazy"
                                  className="w-6 h-6 rounded-full object-cover flex-shrink-0 bg-neutral_1"
                                />
                              )}
                              {option.icon && (
                                <span className="flex-shrink-0">
                                  {React.isValidElement(option.icon)
                                    ? option.icon
                                    : React.createElement(option.icon, { className: "w-4 h-4" })
                                  }
                                </span>
                              )}
                              <span>
                                {option.name || option.label || option[valueField]}
                              </span>
                            </div>
                            {showSelectionTick && isSelected && (
                              <CheckIcon className="w-5 h-5" />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
          </div>

          {/* Search Input at bottom when panel is above */}
          {(searchEnabled || apiMethod) && showSearch && portalPosition.showAbove && (
            <div className={styles.searchContainer}>
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search..."
                value={apiMethod || useExternalSearch ? searchQuery : filterQuery}
                onChange={(e) => {
                  if (apiMethod || useExternalSearch) {
                    setSearchQuery(e.target.value)
                  } else {
                    setFilterQuery(e.target.value)
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter' && filteredOptions.length > 0) {
                        handleCheckboxChange(filteredOptions[0], true);
                    }
                }}
              />
            </div>
          )}
        </div>
      )
    }

    return (
      <div
        className={twMerge(styles.container, className)}
        style={fixedWidth ? { width: fixedWidth } : undefined}
      >
        {/* Trigger (Button or Input) */}
        <div className="flex justify-between items-center relative" ref={buttonRef}>
          {editable ? (
             <div className="relative w-full">
                 <input
                    ref={searchInputRef}
                    type="text"
                    className={twMerge(
                      styles.button,
                      "text-left",
                      showChevron && "pr-10",
                      error && "border-admin_error"
                    )}
                    value={isOpen ? searchQuery : (typeof getDisplayLabel === 'string' ? getDisplayLabel : valueArray.length === 1 ? nameOf(valueArray[0]) : '')}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        if (!isOpen) toggleDropdown();
                    }}
                                    onFocus={() => {
                                        if (!isOpen) toggleDropdown();
                                    }}
                                    onBlur={() => {
                                        // Small delay to allow clicking on dropdown items
                                        setTimeout(() => {
                                            if (!dropdownRef.current?.contains(document.activeElement)) {
                                                setIsOpen(false);
                                                if (creatable && searchQuery) {
                                                    // Check if already selected to avoid redundant updates
                                                    const isCurrent = valueArray.some(v => v[valueField] === searchQuery);
                                                    if (!isCurrent) {
                                                        handleCheckboxChange({ [valueField]: searchQuery, [nameField]: searchQuery }, true);
                                                    }
                                                }
                                            }
                                        }, 200);
                                    }}
                                    onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (creatable) {
                                                // Prioritize typed value if creatable
                                                handleCheckboxChange({ [valueField]: searchQuery, [nameField]: searchQuery }, true);
                                                setIsOpen(false);
                                            } else if (filteredOptions.length > 0) {
                                                // Fallback to first option if strict selection
                                                handleCheckboxChange(filteredOptions[0], true);
                                            }
                                        }
                        if (e.key === 'Escape') {
                            setIsOpen(false);
                        }
                        if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            if (!isOpen) toggleDropdown();
                        }
                    }}
                    placeholder={placeholder}
                 />
                 {showChevron && (
                   <ChevronDownIcon
                     className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                       isOpen ? 'transform rotate-180' : ''
                     }`}
                   />
                 )}
             </div>
          ) : (
          <button
            type="button"
            className={twMerge(
              styles.button,
              error && "border-admin_error",
              (shouldHighlight || isOpen) && styles.highlight,
              customTrigger && "p-0 border-none bg-transparent hover:bg-transparent min-h-0 h-auto w-full flex items-center justify-center", // Minimal styling for custom trigger
              disabled && "pointer-events-none"
            )}
            id="options-menu"
            aria-expanded={isOpen}
            aria-haspopup="true"
            disabled={disabled}
            onClick={toggleDropdown}
          >
            {customTrigger ? (
              customTrigger
            ) : (
            <div className="flex items-center justify-between w-full">
              <span className={twMerge(
                "flex-1 text-left",
                valueArray.length > 0 ? styles.selectedText : styles.placeholderText
              )}>
                {getDisplayLabel}
              </span>
              {showChevron && (!enableResetButton || valueArray.length === 0) && (
                <ChevronDownIcon
                  className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ml-auto ${
                    isOpen ? 'transform rotate-180' : ''
                  }`}
                />
              )}
            </div>
            )}
          </button>
          )}

          {/* Reset Button inside the input */}
          {enableResetButton && valueArray.length > 0 && !editable && (
            <button
              className={styles.resetButton}
              onClick={resetSelection}
              style={{ right: '0.5rem' }} // Adjust position to be inside the button
            >
              &#x2716;
            </button>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="text-admin_error text-sm mt-1">{error}</div>
        )}

        {/* Dropdown panel - always use portal */}
        {isOpen && (options.length > 0 || apiMethod) && (
          createPortal(renderDropdownPanel(), document.body)
        )}
      </div>
    )
  }
)

export default DropDownSelect